import { DeleteOutlined } from '@ant-design/icons';
import { Button, Drawer, Flex, Modal, Tag, Tooltip, message } from 'antd';
import { KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { Schemas } from '@api-client/generated/types';
import { AccessRightsChips } from '@components';
import { LAST_COMPANY_USER_ERROR, useDetachMember } from '@hooks-api';
import { fullName, getApiErrorMessage, roleLabel } from '@utils';

import { CompanyGroup, EmployeeGroup } from './aggregate';
import * as S from './styled';

type Membership = Schemas.AdminMembershipRecordDto;

export type AccessMapTarget =
  | { kind: 'employee'; group: EmployeeGroup }
  | { kind: 'company'; group: CompanyGroup };

type Props = {
  open: boolean;
  target: AccessMapTarget | null;
  onClose: () => void;
};

const AccessMapDrawer = ({ open, target, onClose }: Props) => {
  const navigate = useNavigate();
  const { detach, isDetaching } = useDetachMember();

  // The links are plain <span>s, so wire keyboard activation to match the click.
  const linkProps = (onActivate: () => void) => ({
    role: 'link',
    tabIndex: 0,
    onClick: onActivate,
    onKeyDown: (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onActivate();
      }
    },
  });

  const confirmDetach = (membership: Membership) => {
    Modal.confirm({
      title: 'Remove access?',
      content: `${fullName(membership)} will lose access to ${membership.companyName}.`,
      okText: 'Remove',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await detach(membership.companyId, membership.userId);
          message.success('Access removed');
          // The map refetches via cache invalidation; close so the drawer never
          // shows a removed row.
          onClose();
        } catch (error) {
          if (getApiErrorMessage(error, '') === LAST_COMPANY_USER_ERROR) {
            message.error(
              'This is the last member of the company and can’t be removed'
            );
            return;
          }
          message.error(getApiErrorMessage(error, 'Failed to remove access'));
        }
      },
    });
  };

  const title =
    target?.kind === 'employee'
      ? fullName(target.group)
      : (target?.group.companyName ?? '');

  const memberships = target?.group.memberships ?? [];

  const extra =
    target?.kind === 'company' ? (
      <S.CompanyLink
        {...linkProps(() =>
          navigate(`/companies/${target.group.companyId}/team`)
        )}
      >
        View team
      </S.CompanyLink>
    ) : undefined;

  return (
    <Drawer
      title={title}
      extra={extra}
      open={open}
      onClose={onClose}
      width={420}
      destroyOnClose
    >
      <Flex vertical>
        {memberships.map((membership) => (
          <S.DrawerRow
            key={`${membership.companyId}:${membership.userId}`}
            align="flex-start"
            justify="space-between"
            gap={12}
          >
            <Flex vertical gap={6} flex={1}>
              {target?.kind === 'employee' ? (
                <S.CompanyLink
                  {...linkProps(() =>
                    navigate(`/companies/${membership.companyId}/team`)
                  )}
                >
                  {membership.companyName}
                </S.CompanyLink>
              ) : (
                <Flex align="center" gap={8}>
                  <S.DrawerRowTitle>{fullName(membership)}</S.DrawerRowTitle>
                  <Tag bordered={false}>{roleLabel(membership.adminRole)}</Tag>
                </Flex>
              )}
              <Flex gap={4} wrap="wrap">
                <AccessRightsChips rights={membership.accessRights} />
              </Flex>
            </Flex>
            <Tooltip
              title={
                membership.canRemove
                  ? undefined
                  : 'The last member of a company can’t be removed'
              }
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                disabled={!membership.canRemove || isDetaching}
                onClick={() => confirmDetach(membership)}
                aria-label={`Remove ${fullName(membership)} from ${membership.companyName}`}
              />
            </Tooltip>
          </S.DrawerRow>
        ))}
      </Flex>
    </Drawer>
  );
};

export default AccessMapDrawer;
